// src/lib/layouts/cortesPresets.ts
// Built-in Cortes looks (geometry/text from CapCut combination presets).
// LOCKSTEP: ids/geometry/styles are mirrored in
// autoclipper-backend/src/lib/cortesPresets.js.

import type { CortesConfig, CortesPresetId } from './types';

export interface CortesPreset {
  id: CortesPresetId;
  nameKey: string;
  config: CortesConfig;
}

export const PLACEHOLDER_HEADLINE = 'SUA MANCHETE';
export const PLACEHOLDER_CAPTION = 'SUA LEGENDA';
export const PLACEHOLDER_HANDLE = '@seucanal';
export const CORTES_PLACEHOLDERS = new Set([PLACEHOLDER_HEADLINE, PLACEHOLDER_CAPTION, PLACEHOLDER_HANDLE]);
const RED_SHADOW = { color: '#000000', alpha: 0.9, distance: 5, angle: -45 } as const;

export const CORTES_PRESETS: CortesPreset[] = [
  {
    id: 'caixa-vermelha',
    nameKey: 'projectClips.postLayouts.cortes.presets.caixaVermelha',
    config: {
      kind: 'cortes',
      preset: 'caixa-vermelha',
      headline: PLACEHOLDER_HEADLINE,
      handle: PLACEHOLDER_HANDLE,
      headlineStyle: {
        color: '#ffffff', strokeColor: '#000000', strokeWidth: 0.08,
        shadow: { ...RED_SHADOW }, boxColor: '#ff0000', boxAlpha: 1, boxMode: 'hug', lineSpacing: 0.22,
      },
      handleStyle: { color: '#ffffff' },
      geometry: {
        photo1: { x: 0, y: -0.1185, w: 0.5, h: 0.5 },
        photo2: { x: 0.5, y: -0.1185, w: 0.5, h: 0.5 },
        photo3: { x: 0.02, y: 0.78, w: 0.42, h: 0.2 },
        photo4: { x: 0.56, y: 0.78, w: 0.42, h: 0.2 },
        headline: { x: 0.5, y: 0.25, scale: 0.5668 },
        handle: { x: 0.5, y: 0.70, scale: 0.3804 },
        clip: { x: 0, y: 0.38, w: 1, h: 0.34 },
      },
      headlineBoxWidth: 88,
      headlineFontSize: 60,
      headlineColor: '#ffffff',
    },
  },
  {
    id: 'barra-branca',
    nameKey: 'projectClips.postLayouts.cortes.presets.barraBranca',
    config: {
      kind: 'cortes',
      preset: 'barra-branca',
      headline: PLACEHOLDER_HEADLINE,
      handle: PLACEHOLDER_HANDLE,
      headlineStyle: { color: '#000000', boxColor: '#ffffff', boxAlpha: 1, boxMode: 'bar', lineSpacing: 0.1, strokeWidth: 0 },
      handleStyle: {
        color: '#ffffff',
        strokeColor: '#000000',
        strokeWidth: 0.08,
        shadow: { ...RED_SHADOW },
      },
      geometry: {
        // Under the default subtitles (they end at 70%), off the speaker's face.
        headline: { x: 0.5, y: 0.77, scale: 0.5525 },
        handle: { x: 0.5, y: 0.5383, scale: 0.3008 },
        clip: { x: 0, y: 0, w: 1, h: 1 },
      },
      headlineFontSize: 60,
      headlineColor: '#000000',
    },
  },
];

export function cortesConfigForPreset(preset: CortesPresetId): CortesConfig {
  const source = CORTES_PRESETS.find((p) => p.id === preset);
  if (!source) throw new Error(`unknown cortes preset: ${preset}`);
  const cloned = JSON.parse(JSON.stringify(source.config)) as CortesConfig;
  return {
    ...cloned,
    preset,
    headline: '',
    handle: '',
    backgroundUrl: undefined,
    photo1Url: undefined,
    photo2Url: undefined,
    photo3Url: undefined,
    photo4Url: undefined,
    photo1FillerUrls: undefined,
    photo2FillerUrls: undefined,
    photo3FillerUrls: undefined,
    photo4FillerUrls: undefined,
  };
}
